import { AcquisitionYesNo } from '../../../../services/api/acquisition/acquisitionOperation';
import {
  PrestitoEmployeesNumber,
  PrestitoEmploymentContract,
  PrestitoLoanPurpose,
  PrestitoTenureBand,
} from '../../../../services/api/acquisition/prestitoAcquisition';

/**
 * The Prestito vocabularies exactly as the closed backend schema spells them.
 * The canonical identifier is already Italian prose, so it is also what the
 * operator reads: introducing a prettier label here would be a second
 * vocabulary to keep in step with the contract for no gain.
 */
interface PrestitoOption<TValue extends string> {
  value: TValue;
  label: string;
}

const asOptions = <TValue extends string>(
  values: readonly TValue[]
): PrestitoOption<TValue>[] =>
  values.map((value) => ({ value, label: value }));

/** Every profile the product supports, in the order the public flow lists them. */
export const PRESTITO_EMPLOYMENT_CONTRACT_OPTIONS = asOptions([
  'Dipendente tempo indeterminato',
  'Dipendente tempo determinato',
  'Pensionato',
  'Lavoratore Autonomo/P.iva',
] as const satisfies readonly PrestitoEmploymentContract[]);

export const PRESTITO_TENURE_OPTIONS = asOptions([
  '1 anno o meno',
  '2 anni',
  '3 anni o più',
] as const satisfies readonly PrestitoTenureBand[]);

/**
 * The backend's own employer-size labels. The public UI shows aliases that do
 * not match the contract; reproducing them here would put a second vocabulary
 * between the operator's answer and the value that is judged.
 */
export const PRESTITO_EMPLOYEES_NUMBER_OPTIONS = asOptions([
  'Meno di 15 dip',
  'Più di 15 dip',
] as const satisfies readonly PrestitoEmployeesNumber[]);

export const PRESTITO_LOAN_PURPOSE_OPTIONS = asOptions([
  'Ristrutturazione',
  'Auto',
  'Consolidamento debiti',
  'Spese Mediche',
  'Altro',
] as const satisfies readonly PrestitoLoanPurpose[]);

/** `si`/`no` is the wire vocabulary; Sì/No is what the operator picks. */
export const PRESTITO_YES_NO_OPTIONS: PrestitoOption<AcquisitionYesNo>[] = [
  { value: 'si', label: 'Sì' },
  { value: 'no', label: 'No' },
];

export const prestitoYesNoLabel = (value: AcquisitionYesNo) =>
  value === 'si' ? 'Sì' : 'No';
